import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { CommonModule } from '@angular/common';

// Services
import { UserService } from '../../services/user.service';
import { ReviewService } from '../../services/review.service';
import { ImageUploadService } from '../../services/image-upload.service';
import { AuthService } from '../../services/auth.service';

// Models & Environment
import { UserProfile } from '../../models/UserProfile';
import { Review } from '../../models/Review';
import { environment } from '../../../environments/environment';

// Material Imports
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { StateSnackBarService } from '../../services/state-snack-bar.service';
import { RestStop } from '../../models/rest-stop';
import { MatProgressBar } from '@angular/material/progress-bar';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatGridListModule,
    MatListModule,
    MatDividerModule,
    MatTooltipModule,
    MatButtonModule,
    MatProgressBar,
  ],
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private userService = inject(UserService);
  private reviewService = inject(ReviewService);
  private breakpointObserver = inject(BreakpointObserver);
  private imageUploadService = inject(ImageUploadService);
  private authService = inject(AuthService);
  private stateSnackBarService = inject(StateSnackBarService);

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  protected readonly environment = environment;

  userProfile$!: Observable<UserProfile>;
  userReviews$!: Observable<Review[]>;
  canEdit$!: Observable<boolean>;

  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
    map((result) => result.matches),
    shareReplay(),
  );

  ngOnInit(): void {
    this.userProfile$ = this.route.params.pipe(
      switchMap((params) => this.userService.getUserProfile(params['username'])),
      shareReplay(1),
    );

    this.userReviews$ = this.route.params.pipe(
      switchMap((params) => this.reviewService.getReviewsByUser(params['username'])),
    );

    this.canEdit$ = combineLatest([this.authService.currentUser$, this.userProfile$]).pipe(
      map(([currentUser, viewedProfile]) => {
        if (!currentUser) return false;
        if (currentUser.roles.includes('ROLE_ADMIN')) return true;
        return currentUser.username === viewedProfile.username;
      }),
    );
  }

  openFilePicker(): void {
    this.canEdit$.pipe(take(1)).subscribe((can) => {
      if (can) {
        this.fileInput.nativeElement.value = '';
        this.fileInput.nativeElement.click();
      }
    });
  }

  onFileSelected(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    let fileList: FileList | null = element.files;
    if (fileList && fileList.length > 0) {
      const file = fileList[0];
      this.imageUploadService.uploadProfilePicture(file).subscribe((updatedProfile) => {
        this.authService.updateUserProfile(updatedProfile);
        this.stateSnackBarService.success({ message: 'Profilbild erfolgreich aktualisiert!' });
        this.userProfile$ = this.userService.getUserProfile(this.route.snapshot.params['username']);
      });
    }
  }

  toMap(rs: RestStop) {
    this.router.navigate(['/', rs.slug]);
  }
}
